import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { baseUrl, httpClient } from '../providers/AppDataProvider';

export type CoinImageExtension = 'svg' | 'png';

export interface CoinImageUploadConfig {
  uploadUrl: string;
  fileType: string;
  tags: string;
  pathPrefix: string;
  publicUrlPrefix: string;
  importBaseUrl: string;
}

export interface CoinImageVariantRow {
  extension: CoinImageExtension;
  filename: string;
  uploaded: boolean;
  file: {
    id: string;
    cdnURL?: string;
    publicURL?: string;
    uploadedAt?: string;
    size?: number;
  } | null;
  importSourceUrl: string;
}

export interface CoinImageLookupResult {
  symbol: string;
  variants: CoinImageVariantRow[];
  publicUrlPrefix: string;
}

export interface CoinImageFileRecord {
  id: string;
  filename: string;
  originalName?: string;
  fileType?: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
  cdnURL?: string;
  publicURL?: string;
}

export interface CoinImageListResult {
  data: CoinImageFileRecord[];
  total: number;
  page?: number;
  size?: number;
}

export interface CoinSymbolOverviewItem {
  symbol: string;
  hycoinUrl: string;
  svgUrl: string;
  pngUrl: string;
}

export interface CoinSymbolOverviewResult {
  items: CoinSymbolOverviewItem[];
  total: number;
}

export interface ReuploadCoinImageResult {
  symbol: string;
  types: CoinImageExtension[];
  sourceUrl?: string;
  deleted?: Partial<Record<CoinImageExtension, string>>;
  svg?: unknown;
  png?: unknown;
}

function unwrap<T>(json: unknown): T {
  if (json && typeof json === 'object' && 'data' in (json as object)) {
    return (json as { data: T }).data;
  }
  return json as T;
}

export async function fetchCoinImageUploadConfig(): Promise<CoinImageUploadConfig> {
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/upload-config`);
  return unwrap<CoinImageUploadConfig>(json);
}

export async function lookupCoinImageBySymbol(symbol: string): Promise<CoinImageLookupResult> {
  const qs = new URLSearchParams({ symbol: symbol.trim() });
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/lookup?${qs}`);
  return unwrap<CoinImageLookupResult>(json);
}

export async function uploadCoinImage(file: File, customFilename?: string): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file);
  if (customFilename?.trim()) {
    formData.append('customFilename', customFilename.trim());
  }

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
    Accept: 'application/json',
  };

  const response = await axios.post(`${baseUrl}/cdn/coin-images/upload`, formData, { headers });
  return response.data;
}

export async function importCoinImageFromUrl(body: {
  sourceUrl: string;
  symbol?: string;
  extension?: CoinImageExtension;
}): Promise<unknown> {
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/import`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return unwrap(json);
}

export async function resetCoinImageBySymbol(symbol: string): Promise<unknown> {
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/reset-by-symbol`, {
    method: 'POST',
    body: JSON.stringify({ symbol: symbol.trim() }),
  });
  return unwrap(json);
}

export async function deleteCoinImage(id: string): Promise<unknown> {
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/${id}`, { method: 'DELETE' });
  return unwrap(json);
}

export function probeImageUrl(url: string, timeoutMs = 8_000): Promise<boolean> {
  const trimmed = url?.trim();
  if (!trimmed) return Promise.resolve(false);

  return new Promise((resolve) => {
    const img = new Image();//image reload event 预加载判断
    const timer = window.setTimeout(() => resolve(false), timeoutMs);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    img.src = trimmed;
  });
}

export async function fetchCoinSymbolOverview(params: {
  symbol?: string;
  limit?: number;
  offset?: number;
}): Promise<CoinSymbolOverviewResult> {
  const qs = new URLSearchParams();
  const symbol = params.symbol?.trim();
  if (symbol) qs.set('symbol', symbol);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/overview?${qs}`);
  return unwrap<CoinSymbolOverviewResult>(json);
}

export async function reuploadCoinImageFromHyperliquid(
  symbol: string,
  types: CoinImageExtension[],
): Promise<ReuploadCoinImageResult> {
  const { json } = await httpClient(`${baseUrl}/cdn/coin-images/reupload`, {
    method: 'POST',
    body: JSON.stringify({ symbol: symbol.trim(), types }),
  });
  return unwrap<ReuploadCoinImageResult>(json);
}
